import { formatVariantLabel, variantTheme, type VariantTheme } from 'lib/format-variant';

export interface VariantAssetRecord {
  code: string;
  card_back_style?: string | null;
  cardBackStyle?: string | null;
  table_theme_color?: string | null;
  tableThemeColor?: string | null;
  theme_audio_url?: string | null;
  themeAudioUrl?: string | null;
}

const assetCache = new Map<string, VariantAssetRecord>();

export function cacheVariantAssets(variants: VariantAssetRecord[]): void {
  assetCache.clear();
  for (const variant of variants) {
    assetCache.set(variant.code.trim().toLowerCase(), variant);
  }
}

export function getVariantAssets(code: string | null | undefined): VariantAssetRecord | undefined {
  return assetCache.get((code || 'classic').trim().toLowerCase());
}

export function variantTableThemeColor(code: string | null | undefined): string | undefined {
  const assets = getVariantAssets(code);
  const color = assets?.table_theme_color ?? assets?.tableThemeColor;
  return color && /^#[0-9A-Fa-f]{6}$/.test(color) ? color : undefined;
}

export function variantCardBackStyle(code: string | null | undefined): string | undefined {
  const assets = getVariantAssets(code);
  const style = assets?.card_back_style ?? assets?.cardBackStyle;
  return style?.trim() || undefined;
}

export function variantThemeAudioUrl(code: string | null | undefined): string | undefined {
  const assets = getVariantAssets(code);
  const url = assets?.theme_audio_url ?? assets?.themeAudioUrl;
  return url?.trim() || undefined;
}

/** Apply admin-configured table theme color to the document root (fallback to preset theme). */
export function applyVariantThemeCss(code: string | null | undefined): VariantTheme {
  const preset = variantTheme(code);
  const color = variantTableThemeColor(code);
  if (color) {
    document.documentElement.style.setProperty('--tp-variant-table-color', color);
  } else {
    document.documentElement.style.removeProperty('--tp-variant-table-color');
  }
  document.documentElement.dataset.variantTheme = preset;
  return preset;
}

export function lobbyPathForVariant(code: string | null | undefined): string {
  return `/lobby?variant=${encodeURIComponent(formatVariantLabel(code))}`;
}

const LAST_VARIANT_KEY = 'tp_last_variant';

export function rememberLastVariant(code: string | null | undefined): void {
  try {
    sessionStorage.setItem(LAST_VARIANT_KEY, formatVariantLabel(code));
  } catch {
    /* ignore */
  }
}

export function readLastVariant(): string {
  try {
    return sessionStorage.getItem(LAST_VARIANT_KEY) || 'Classic';
  } catch {
    return 'Classic';
  }
}

let themeAudio: HTMLAudioElement | null = null;

export function playVariantThemeAudio(code: string | null | undefined): void {
  const url = variantThemeAudioUrl(code);
  if (!url) return;
  try {
    if (themeAudio) {
      themeAudio.pause();
      themeAudio = null;
    }
    themeAudio = new Audio(url);
    themeAudio.loop = true;
    themeAudio.volume = 0.35;
    void themeAudio.play().catch(() => {
      /* autoplay blocked until user gesture */
    });
  } catch {
    /* ignore */
  }
}

export function stopVariantThemeAudio(): void {
  if (!themeAudio) return;
  themeAudio.pause();
  themeAudio = null;
}
